/**
 * Contract Service
 * Handles contract-related API calls
 */
import BaseApiService from '../base/BaseApiService';
import type {
  Contract,
  ContractFilters,
  ContractRelatedMeeting,
  ContractsResponse,
  CreateContractRequest,
  UpdateContractRequest
} from '@app-types';
import type { PendingScopeContract } from '@app-types/import';

/** UI column keys → backend search column names. List search still always includes CO/CM. */
const CONTRACT_SEARCH_COLUMN_MAP: Record<string, string> = {
  owner: 'contract_owner',
  manager: 'contract_manager',
  supplier: 'supplier'
};

function mapContractSearchColumns(columns?: string[]): string[] | undefined {
  if (!columns || columns.length === 0) return undefined;
  return columns.map((column) => CONTRACT_SEARCH_COLUMN_MAP[column] ?? column);
}

class ContractService extends BaseApiService {
  /**
   * Get contracts list with optional filters
   */
  async getContracts(filters: ContractFilters = {}): Promise<ContractsResponse> {
    const params = this.buildQueryParams({
      ...filters,
      searchColumns: mapContractSearchColumns(filters.searchColumns)
    });
    return this.get<ContractsResponse>(`/contracts?${params}`);
  }

  /**
   * Get all contracts matching the given filters, ignoring pagination — used for
   * full-dataset export.
   */
  async getContractsForExport(
    filters: Omit<ContractFilters, 'limit' | 'offset'> = {}
  ): Promise<{ total: number; data: Contract[] }> {
    const params = this.buildQueryParams({
      ...filters,
      searchColumns: mapContractSearchColumns(filters.searchColumns)
    });
    return this.get<{ total: number; data: Contract[] }>(`/contracts/export?${params}`);
  }

  /**
   * Get a single contract by ID
   */
  async getContract(id: number): Promise<Contract> {
    return this.get<Contract>(`/contracts/${id}`);
  }

  /**
   * Get available contract types
   */
  async getContractTypes(): Promise<string[]> {
    return this.get<string[]>('/contracts/contract-types');
  }

  /**
   * Create a new contract
   */
  async createContract(data: CreateContractRequest): Promise<{ message: string; contract: Contract }> {
    return this.post<{ message: string; contract: Contract }>('/contracts', data);
  }

  /**
   * Update an existing contract
   */
  async updateContract(id: number, data: UpdateContractRequest): Promise<Contract> {
    return this.put<Contract>(`/contracts/${id}`, data);
  }

  /**
   * Reassociate a contract with a different supplier
   */
  async reassociateContractSupplier(
    contractId: number,
    supplierId: number
  ): Promise<{
    message: string;
    contract: {
      id: number;
      contract_number: string;
      old_supplier: { id: number; name: string };
      new_supplier: { id: number; name: string };
    };
  }> {
    return this.post<{
      message: string;
      contract: {
        id: number;
        contract_number: string;
        old_supplier: { id: number; name: string };
        new_supplier: { id: number; name: string };
      };
    }>(`/contracts/${contractId}/reassociate-supplier`, { supplier_id: supplierId });
  }

  async getPendingScopeContracts(): Promise<{ total: number; data: PendingScopeContract[] }> {
    return this.get<{ total: number; data: PendingScopeContract[] }>('/contracts/pending-scope');
  }

  async applyPendingScope(contractId: number, reason?: string): Promise<Contract> {
    return this.post<Contract>(`/contracts/${contractId}/pending-scope/apply`, { reason });
  }

  async dismissPendingScope(contractId: number, reason?: string): Promise<Contract> {
    return this.post<Contract>(`/contracts/${contractId}/pending-scope/dismiss`, { reason });
  }

  /**
   * Related meetings for a contract, newest first (ADO-719)
   */
  async getContractMeetings(id: number): Promise<ContractRelatedMeeting[]> {
    const response = await this.get<{ data: ContractRelatedMeeting[] }>(`/contracts/${id}/meetings`);
    return response.data;
  }

  /**
   * Hard-delete a contract with no linked meetings or assessments (ADO-743)
   */
  async deleteContract(id: number): Promise<void> {
    await this.delete(`/contracts/${id}`);
  }
}

export const contractService = new ContractService();
export default contractService;
